import { useState } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useServerStore } from "../../../stores/serverStore.js";
import { useChannelStore } from "../../../stores/channelStore.js";

const NOTIF_LEVELS = [
  { value: 'all',      label: 'All Messages',  icon: '🔔', desc: 'Every message' },
  { value: 'mentions', label: 'Mentions Only',  icon: '🔕', desc: '@mentions and DMs' },
  { value: 'nothing',  label: 'Nothing',        icon: '🚫', desc: 'No notifications' },
];

const STORE_KEY = 'notif_prefs';
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch { return {}; }
}
function savePrefs(p) { localStorage.setItem(STORE_KEY, JSON.stringify(p)); }

export function NotificationPrefsModal() {
  const { isOpen, type, onClose } = useModalStore();
  const servers  = useServerStore(s => s.servers || []);
  const channels = useChannelStore(s => s.channels || []);

  const [prefs, setPrefs] = useState(loadPrefs);
  const [tab,   setTab]   = useState('server'); // 'server' | 'channel' | 'push'
  const [saved, setSaved] = useState(false);

  if (!isOpen || type !== 'notifPrefs') return null;

  const update = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    savePrefs(next);
  };

  const handleSave = () => { savePrefs(prefs); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const textChannels = channels.filter(c => c.type !== 'voice').slice(0, 20);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🔔 Notification Preferences</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            {[['server', '🖥️ Servers'], ['channel', '# Channels'], ['push', '📱 Push']].map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 700 : 400,
                  color: tab === t ? '#fff' : 'rgba(255,255,255,0.4)', borderBottom: `2px solid ${tab === t ? 'var(--color-primary,#6366f1)' : 'transparent'}`, marginBottom: -1, transition: 'all 0.15s' }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* Server-level overrides */}
          {tab === 'server' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
                Set a default notification level for each server.
              </div>
              {/* Global default */}
              <NotifRow
                label="Default (all servers)"
                sub="Applied where no override is set"
                value={prefs.__global__ || 'mentions'}
                onChange={v => update('__global__', v)}
                bold
              />
              <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '8px 0' }} />
              {servers.slice(0, 20).map(s => (
                <NotifRow key={s.id}
                  label={s.name}
                  sub={`#${s.id?.slice(0, 8)}`}
                  value={prefs[`srv:${s.id}`] || 'inherit'}
                  onChange={v => update(`srv:${s.id}`, v)}
                  hasInherit
                />
              ))}
              {servers.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No servers joined</div>}
            </div>
          )}

          {/* Channel-level overrides */}
          {tab === 'channel' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
                Override notifications for specific channels. Takes priority over server settings.
              </div>
              {textChannels.map(c => (
                <NotifRow key={c.id}
                  label={`#${c.name}`}
                  sub={c.topic || c.category || ''}
                  value={prefs[`ch:${c.id}`] || 'inherit'}
                  onChange={v => update(`ch:${c.id}`, v)}
                  hasInherit
                />
              ))}
              {textChannels.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No channels available</div>}
            </div>
          )}

          {/* Push settings */}
          {tab === 'push' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <ToggleRow label="Browser Notifications" sub="Show desktop push notifications" checked={!!prefs.push_browser} onChange={v => update('push_browser', v)} />
              <ToggleRow label="Sound Alerts" sub="Play a sound on new messages" checked={prefs.push_sound !== false} onChange={v => update('push_sound', v)} />
              <ToggleRow label="@mention Badge" sub="Show a badge for unread mentions" checked={prefs.push_badge !== false} onChange={v => update('push_badge', v)} />
              <ToggleRow label="DM Notifications" sub="Always notify on direct messages" checked={prefs.push_dm !== false} onChange={v => update('push_dm', v)} />
              <ToggleRow label="Friend Requests" sub="Notify when someone adds you" checked={prefs.push_friends !== false} onChange={v => update('push_friends', v)} />
              <ToggleRow label="Server Events" sub="Remind me about upcoming events" checked={!!prefs.push_events} onChange={v => update('push_events', v)} />
              <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Quiet Hours</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>From</span>
                  <input type="time" value={prefs.quiet_from || '22:00'} onChange={e => update('quiet_from', e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none' }} />
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>to</span>
                  <input type="time" value={prefs.quiet_to || '08:00'} onChange={e => update('quiet_to', e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer' }}>Close</button>
          <button onClick={handleSave}
            style={{ padding: '8px 20px', borderRadius: 8, background: saved ? '#22c55e' : 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}>
            {saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotifRow({ label, sub, value, onChange, bold, hasInherit }) {
  const opts = hasInherit
    ? [{ value: 'inherit', label: 'Inherit' }, ...NOTIF_LEVELS]
    : NOTIF_LEVELS;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
      <div>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: bold ? 700 : 400 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{sub}</div>}
      </div>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
        {opts.map(o => <option key={o.value} value={o.value}>{o.icon ? `${o.icon} ` : ''}{o.label}</option>)}
      </select>
    </div>
  );
}

function ToggleRow({ label, sub, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{sub}</div>}
      </div>
      <button type="button" onClick={() => onChange(!checked)}
        style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', transition: 'background 0.2s', position: 'relative', background: checked ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.15)' }}>
        <span style={{ position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: checked ? 23 : 3 }} />
      </button>
    </div>
  );
}
